import { GroupIcon } from "shared/GroupIcon";
import { TranslatedTextField } from "shared/localizedText";
import { LocalizedText, PRIMARY_LOCALE } from "shared/localizedText";
import React, { useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { css } from "@emotion/css";
import { Breakpoint } from "theme";
import { CataloguePath } from "routes/createContent/paths";
import ProgressButton from "shared/ProgressButton";
import { useCreateTemplate } from "routes/createContent/api/useCreateTemplate";
import { useUpdateTemplate } from "routes/createContent/api/useUpdateTemplate";
import { CatalogueRollout } from "routes/createContent/api/catalogueDeployments";
import { CatalogueDeploymentRow } from "routes/createContent/components/CatalogueDeploymentRow";
import { EditorToolbar } from "routes/createContent/components/EditorToolbar";
import { TemplateFormData, formToUpsertBody } from "./types";
import {
  EXERCISES_PER_DAY_OPTIONS,
  GROUP_OPTIONS,
  HEALTH_FORM_TYPE_OPTIONS,
  MAX_DURATION_WEEKS,
  humanize,
} from "./constants";
import ExercisesSection from "./components/ExercisesSection";
import LessonsSection from "./components/LessonsSection";

type Props = {
  initialValues: TemplateFormData;
  rollout?: CatalogueRollout;
  readOnly?: boolean;
};

const TemplateEditor: React.VFC<Props> = ({ initialValues, rollout, readOnly = false }) => {
  const history = useHistory();
  // shouldUnregister: false keeps non-Controller fields (ids, per-item
  // title/image/duration metadata) in the form value so getValues() returns the
  // full shape — same reasoning as the lesson editor.
  const form = useForm<TemplateFormData>({ defaultValues: initialValues, shouldUnregister: false });
  const { control, register, handleSubmit, getValues } = form;
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const isEditing = initialValues.id != null;
  const saving = create.isLoading || update.isLoading;
  const saveError = create.error || update.error;
  const saveErrorMessage =
    (saveError as { response?: { status?: number } } | null)?.response?.status === 409
      ? "That key is already taken by another template."
      : "Failed to save template.";

  useEffect(() => {
    register("id");
  }, [register]);

  const ongoing = useWatch({ control, name: "ongoing" }) as boolean;
  const name = useWatch({ control, name: "name" }) as LocalizedText;
  const group = useWatch({ control, name: "group" }) as string;
  const exercises = useWatch({ control, name: "exercises" }) as unknown[] | undefined;
  const key = useWatch({ control, name: "key" }) as string | null;
  const canSave = !!name?.[PRIMARY_LOCALE.code]?.trim() && !!group && !!key && (exercises?.length ?? 0) > 0;

  const onSubmit = handleSubmit(async () => {
    const data = getValues();
    const body = formToUpsertBody(data);
    if (!body) {
      return;
    }
    try {
      if (isEditing && data.id != null) {
        await update.mutateAsync({ id: data.id, body });
        form.reset(data);
        return;
      }
      const created = await create.mutateAsync(body);
      history.replace(CataloguePath.Templates_Edit.replace(":id", String(created.id)));
    } catch {
      // Keep the current form state so the user can retry.
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit}>
        <EditorToolbar
          actions={
            <>
              {isEditing && key && (
                <CatalogueDeploymentRow
                  kind="template"
                  name={key}
                  rollout={rollout}
                  unsavedChanges={form.formState.isDirty}
                />
              )}
              {!readOnly && (
                <ProgressButton size="small" type="submit" loading={saving} disabled={saving || !canSave}>
                  {isEditing ? "Save" : "Create"}
                </ProgressButton>
              )}
            </>
          }
        >
          <Tooltip title="Back to templates">
            <IconButton size="small" onClick={() => history.push(CataloguePath.Templates)} aria-label="Back">
              <ArrowBack />
            </IconButton>
          </Tooltip>

          <div className={styles.groupIcon}>
            <GroupIcon group={group} />
          </div>

          <div className={styles.headerName}>
            <Controller
              control={control}
              name="name"
              render={({ onChange, value }) => (
                <TranslatedTextField label="Template name" value={value} onChange={onChange} disabled={readOnly} />
              )}
            />
          </div>

          {isEditing && key && <code className={styles.keyChip}>{key}</code>}
        </EditorToolbar>

        {!readOnly && !canSave && (
          <Typography variant="caption" color="textSecondary" component="div" className={styles.hint}>
            A template needs an English (US) name, a key, a group and at least one exercise.
          </Typography>
        )}
        {saveError && (
          <Typography variant="caption" color="error" component="div" className={styles.hint}>
            {saveErrorMessage}
          </Typography>
        )}

        <div className={styles.body}>
          <Paper variant="outlined" className={styles.settings}>
            <Typography variant="overline" color="textSecondary">
              Settings
            </Typography>

            {!isEditing && (
              <Controller
                control={control}
                name="key"
                render={({ onChange, value }) => (
                  <TextField
                    label="Key"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    disabled={readOnly}
                    value={value ?? ""}
                    onChange={e => onChange(e.target.value)}
                    helperText="Permanent identifier the rest of the system looks this template up by. Must be unique."
                  />
                )}
              />
            )}

            <Controller
              control={control}
              name="group"
              render={({ onChange, value }) => (
                <FormControl variant="outlined" size="small" required fullWidth disabled={readOnly}>
                  <InputLabel>Group</InputLabel>
                  <Select label="Group" value={value} onChange={e => onChange(e.target.value)}>
                    {GROUP_OPTIONS.map(g => (
                      <MenuItem key={g} value={g}>
                        {humanize(g)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="health_form_type"
              render={({ onChange, value }) => (
                <FormControl variant="outlined" size="small" fullWidth disabled={readOnly}>
                  <InputLabel>Questionnaire (health form)</InputLabel>
                  <Select label="Questionnaire (health form)" value={value} onChange={e => onChange(e.target.value)}>
                    {HEALTH_FORM_TYPE_OPTIONS.map(h => (
                      <MenuItem key={h} value={h}>
                        {humanize(h)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="exercises_per_day"
              render={({ onChange, value }) => (
                <FormControl variant="outlined" size="small" fullWidth disabled={readOnly}>
                  <InputLabel>Exercises per day</InputLabel>
                  <Select label="Exercises per day" value={value} onChange={e => onChange(Number(e.target.value))}>
                    {EXERCISES_PER_DAY_OPTIONS.map(n => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="ongoing"
              render={({ onChange, value }) => (
                <FormControlLabel
                  control={
                    <Checkbox checked={!!value} disabled={readOnly} onChange={e => onChange(e.target.checked)} />
                  }
                  label="Ongoing program"
                />
              )}
            />
            {!ongoing && (
              <Controller
                control={control}
                name="duration_weeks"
                render={({ onChange, value }) => (
                  <TextField
                    label="Program length (weeks)"
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    inputProps={{ min: 1, max: MAX_DURATION_WEEKS }}
                    value={value}
                    onChange={e => onChange(Number(e.target.value))}
                  />
                )}
              />
            )}

            <Controller
              control={control}
              name="includes_functional_test"
              render={({ onChange, value }) => (
                <FormControlLabel
                  control={
                    <Checkbox checked={!!value} disabled={readOnly} onChange={e => onChange(e.target.checked)} />
                  }
                  label="Includes functional test"
                />
              )}
            />
          </Paper>

          <Box>
            <ExercisesSection readOnly={readOnly} />
            <LessonsSection readOnly={readOnly} />
          </Box>
        </div>
      </form>
    </FormProvider>
  );
};

const styles = {
  groupIcon: css`
    display: flex;
    width: 40px;
    height: 40px;
    & > svg {
      width: 100%;
      height: 100%;
    }
  `,
  headerName: css`
    flex: 1 1 260px;
    max-width: 420px;
  `,
  keyChip: css`
    padding: 3px 8px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.6);
  `,
  hint: css`
    margin-bottom: 8px;
  `,
  body: css`
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 24px;
    align-items: start;

    @media screen and (max-width: ${Breakpoint.md}px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  settings: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    position: sticky;
    top: 68px;

    @media screen and (max-width: ${Breakpoint.md}px) {
      position: static;
    }
  `,
};

export default TemplateEditor;
